import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import React, {useContext, useEffect, useMemo, useState} from "react";
import {ColumnProps} from "antd/lib/table";
import {ISort, IStaticFilter} from "../../../models/base/base-query";
import {tableOnChange} from "../../../utils/helpers/table-sorts-filters";
import Table from "../../general/table-components/table";
import Space from "../../general/antd/space";
import {Button} from "antd";
import {BarsOutlined} from "@ant-design/icons";
import ViewBtn from "../../general/table-components/actions/view-btn";
import EditBtn from "../../general/table-components/actions/edit-btn";
import DeleteBtn from "../../general/table-components/actions/delete-btn";
import eventManager, {EVENT_SUCCESS} from "../../../utils/events";
import {arrayMove, SortableContext, useSortable, verticalListSortingStrategy} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";
import {isEmpty} from "lodash";
import EndPoints from "../../../services/end-points";
import {closestCenter, DndContext, DragEndEvent} from "@dnd-kit/core";
import CategoryCampaignContext from "../../../context/category-campaign/context";
import {ICategoryCampaign} from "../../../models/category_campaign/response";

interface RowContextProps {
    setActivatorNodeRef?: (element: HTMLElement | null) => void;
    listeners?: any;
}

const RowContext = React.createContext<RowContextProps>({});


const DragHandle = ({isSortingEnabled}: { isSortingEnabled: boolean }) => {
    const {setActivatorNodeRef, listeners} = useContext(RowContext);

    return (
        <Button
            type="text"
            size="small"
            icon={<BarsOutlined/>}
            style={{
                cursor: isSortingEnabled ? "grab" : "not-allowed",
                opacity: isSortingEnabled ? 1 : 0.5,
            }}
            ref={setActivatorNodeRef}
            {...(isSortingEnabled ? listeners : {})}
            disabled={!isSortingEnabled}
        />
    );
};

const Row: React.FC<{ "data-row-key": string } & React.HTMLAttributes<HTMLTableRowElement>> = (props) => {
    const {attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging} = useSortable({
        id: props["data-row-key"],
    });

    const style: React.CSSProperties = {
        ...props.style,
        transform: CSS.Translate.toString(transform),
        transition,
        ...(isDragging ? {position: "relative", zIndex: 9999} : {}),
    };

    const contextValue = useMemo(() => ({setActivatorNodeRef, listeners}), [setActivatorNodeRef, listeners]);

    return (
        <RowContext.Provider value={contextValue}>
            <tr {...props} ref={setNodeRef} style={style} {...attributes} />
        </RowContext.Provider>
    );
};

interface IProps {
    isSortingEnabled: boolean;
    setLoadingCat: (loadingCat: boolean) => void
}

const CategoryCampaignTable: React.FC<IProps> = ({isSortingEnabled, setLoadingCat}) => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {list, actions, loading, query} = useContext(CategoryCampaignContext);
    const [dataSource, setDataSource] = useState(list?.data ?? []);
    const [loadingUpdate, setLoadingUpdate] = useState(true);

    useEffect(() => {
        if (list?.data) {
            setDataSource(list.data);
            setLoadingUpdate(false);
            setLoadingCat(false);
        }
    }, [list?.data]);

    const onDragEnd = async ({ active, over }: DragEndEvent) => {
        if (!isSortingEnabled) return;
        if (!over) return;

        const activeId = Number(active.id);
        const overId = Number(over.id);

        const movedItem = dataSource.find((item) => item.id === activeId);
        const overItem = dataSource.find((item) => item.id === overId);

        if (movedItem && overItem) {
            const updatedDataSource = arrayMove(dataSource, dataSource.indexOf(movedItem), dataSource.indexOf(overItem));
            setDataSource(updatedDataSource);

            await handlePriorityChange(movedItem.id, overItem.priority);
        }
    };

    const handlePriorityChange = async (id: number, newPriority: number | null) => {
        if (newPriority === null) return;

        const updatedDataSource = dataSource.map((item) =>
            item.id === id ? { ...item, priority: newPriority } : item
        );

        const sortedDataSource = [...updatedDataSource].sort((a, b) => a.priority - b.priority);
        setDataSource(sortedDataSource);

        await actions.updateCategoriesArrangementList(id, { priority: newPriority });
        await actions.getData();
    };

    const columns: ColumnProps<ICategoryCampaign>[] = [
        { key: "sort", align: "center", width: 80, render: () => <DragHandle isSortingEnabled={isSortingEnabled} /> },
        {
            title: t("id"),
            dataIndex: "id",
            key: "id",
            sorter: {
                multiple: 1,
            },
            width: 100,
            align: "center",
        },
        {
            title: t("name"),
            dataIndex: "name",
            align: "center",
            key: "name",
            sorter: {
                multiple: 2,
            },
        },
        {
            title: t("priority"),
            dataIndex: "priority",
            key: "priority",
            align: "center",
        },
        {
            title: t("actions"),
            dataIndex: "",
            width: 250,
            fixed: "right",
            align: "center",
            key: "x",
            render: (_: any, record) => (
                <Space>
                    <ViewBtn
                        onClick={async () => {
                            navigate(`details/${record?.id}`);
                        }}
                    />
                    <EditBtn
                        onClick={async () => {
                            navigate(`update/${record?.id}`);
                        }}
                    />
                    <DeleteBtn
                        onConfirm={async () => {
                            await actions.deleteCategoryCampaign(record?.id);
                            eventManager.emit(EVENT_SUCCESS);
                        }}
                        loading={loading.includes("delete")}
                    />
                </Space>
            ),
        },
    ];

    let tableFiltersProps = {};
    const [sorts, setSorts] = useState<ISort[]>([]);
    const [, setStaticFilters] = useState<IStaticFilter[]>(query?.filters ?? []);

    useEffect(() => {
        sorts.length > 0 &&
        actions.setQuery({
            ...query,
            orders: sorts,
        });
    }, [sorts]);

    return (
        <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={dataSource.map((i) => i.id)} strategy={verticalListSortingStrategy}>
                <Table<any>
                    rowKey="id"
                    showPagination={true}
                    columns={columns}
                    dataSource={dataSource}
                    loading={loading.includes("list")}
                    total={list?.total}
                    size="small"
                    scroll={{
                        x: 1400,
                    }}
                    pageSize={query.perPage}
                    page={query.page}
                    onPaginationChange={(page, pageSize) => {
                        actions.setQuery({
                            ...query,
                            page,
                            perPage: pageSize,
                        });
                    }}
                    onChange={(_, tableFilters, tabelSorters) => {
                        tableOnChange(
                            tableFilters,
                            tabelSorters,
                            tableFiltersProps,
                            setSorts,
                            setStaticFilters
                        );
                    }}
                    components={{
                        body: {
                            row: Row,
                        },
                    }}
                />
            </SortableContext>
        </DndContext>
    )
}
export default CategoryCampaignTable;
